<template>
  <v-card id="tract-contract-form">

    <v-card-title class="primary white--text">
      <span v-html="title"/>
      <v-spacer/>
      <BaseDialogActions hideRefresh elementId="tract-contract-form-dialog-actions"/>
    </v-card-title>

    <v-card-text>
      <v-container fluid>
        <v-alert
          v-if="missingContractRequirements.length > 0"
          type="warning"
          outlined
          text
          dense
          id="tract-contract-missing-requirements">
          {{ $t('tractMissingContractRequirements', { fields: requiredFieldMessages(missingContractRequirements) }) }}
          <div v-if="hasUnsavedRequirementChanges" class="mt-1">{{ $t('tractMissingContractRequirementsUnsaved') }}</div>
        </v-alert>
        <v-row dense>
          <v-col cols="12" md="6" :lg="isByproductPurchaseContract ? 4 : 3">
            <v-select
              :items="contractTypes"
              :label="$t('contractType')"
              v-model="contractType"
              return-object
              value="name"
              item-text="name"
              color="black">
            </v-select>
          </v-col>

          <v-col cols="12" md="6" :lg="isByproductPurchaseContract ? 4 : 3">
            <AccountAutocomplete
              :accountId="tract?.contractAccountId"
              :title="$t('account')"
              hideRefresh
              :ticketBookAccounts="tractTicketBookAccounts"
              :nonTicketBookAccountTooltip="nonTicketBookAccountTooltip"
              @account-chosen="accountChosen"
              data-testid="contract-tract-account"
              showCertified
            />
          </v-col>

          <v-col v-if="!isByproductPurchaseContract" cols="12" md="6" lg="3">
            <SettingAutocomplete
              :settingId="tract?.contractSettingId"
              @setting-chosen="settingChosen"
              hideRefresh
              :wantAutofill="true"
              data-testid="contract-tract-setting"
            />
          </v-col>

          <v-col cols="12" md="6" :lg="isByproductPurchaseContract ? 4 : 3">
            <v-autocomplete
              v-model="selectedBundle"
              :loading="templatesLoading || (namedRates === undefined && isInTractGroup)"
              :items="mappedTemplateBundlesWithWarning"
              :label="$t('templateBundles')"
              :hint="$t('selectBundleToUse')"
              persistent-hint
              return-object
              item-text="name"
              item-value="name"
              :item-disabled="item => item.missingObligationRate"
              @change="templateSelected"
            >
              <template #item="{item, attrs}">
                <div v-bind="attrs" style="width: 100%; pointer-events: none;">
                  {{ item.name }}
                  <Icon
                  icon="mdi-alert"
                  iconColor="warning"
                  v-if="item.missingTractFieldsString"
                  :tooltipText="$t('missingNecessaryTractFields', { fields: item.missingTractFieldsString })"
                  style="line-height: 0; pointer-events: auto;"/>
                  <Icon
                  icon="mdi-tag-multiple-outline"
                  iconColor="error"
                  :small="false"
                  v-if="item.missingObligationRate"
                  :tooltipText="$t('missingObligationRate')"
                  style="pointer-events: auto;"/>
                </div>
              </template>

              <template #selection="{item}">
                {{ item.name }}
                <Icon
                icon="mdi-alert"
                iconColor="warning"
                v-if="item.missingTractFieldsString"
                :tooltipText="$t('missingNecessaryTractFields', { fields: item.missingTractFieldsString })"
                style="line-height: 0;"/>
                <Icon
                icon="mdi-tag-multiple-outline"
                iconColor="error"
                :small="false"
                v-if="item.missingObligationRate"
                :tooltipText="$t('missingObligationRate')"/>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row v-if="selectedBundle">
          <v-col cols="12" md="6">
            <ProductSelector
            v-if="!loading"
            :propProducts="contractProducts"
            :availableProducts="contractProducts"
            :hint="$t('tractDestinationProducts')"
            label="contractProducts"
            @products-selected="contractProductsSelected"/>
          </v-col>
          <v-col cols="12" md="auto">
            <v-checkbox
              v-if="showScaleTicketCheckbox"
              id="tract-destination-contract-scales-tickets"
              v-model="scalesTickets"
              color="primary"
              :label="$t('defineScaleFrequency')"
            />
          </v-col>
          <v-col cols="12" md="auto">
            <v-checkbox
              v-model="limitToTractLevelCapture"
              color="primary"
              :label="$t('onlyShowTractLevelInput')"
            />
          </v-col>
          <v-col cols="12" md="auto">
            <v-checkbox
              v-if="isCruiseRateCheckboxVisible"
              v-model="useCruiseRateForStumpage"
              color="primary"
              :label="$t('useCruiseRateForStumpage')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="8" v-if="scalesTickets" class="pa-0 ma-0">
            <v-card flat class="pa-0 ma-0">
              <v-card-title class="subtitle-1 px-0">
                <span>{{ $t('scaleSampleFrequency') }}</span>
                <Icon
                  icon="mdi-information-outline"
                  small
                  class="ml-2"
                  iconColor="grey"
                  :tooltipText="$t('scaleSampleFrequencyDescription')"
                />
                <Icon
                  v-if="showIncompatibleProductWarning"
                  icon="mdi-alert"
                  small
                  class="ml-1"
                  iconColor="warning"
                  :tooltipText="$t('incompatibleScalingProducts')"
                />
              </v-card-title>
              <v-card-text class="px-0">
                <v-row dense>
                  <v-col
                    v-for="product in eligibleScalingProducts"
                    :key="product.productId"
                    cols="12" sm="6" md="4" lg="3"
                  >
                    <v-card outlined class="pa-3">
                      <div class="mb-2">{{ product.name }}</div>
                      <v-text-field
                        v-model="product.scaleFrequency"
                        :rules="[rules.positiveIntegerBetweenOrBlank(0, 100)]"
                        dense
                        hide-details="auto"
                        color="primary"
                        type="number"
                        hide-spin-buttons
                      >
                        <template #prepend-inner>
                          <span class="grey--text body-2 mt-1">1:</span>
                        </template>
                      </v-text-field>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="selectedBundle">
          <v-col cols="12" :lg="showGrossProfit ? 10 : 12" :xl="showGrossProfit ? 10 : 12">
            <LoadableComponent :loading="loading">
              <BundleActivityDefinition
                :tract="tract"
                :tractDestination="tractDestination"
                :propBundle="selectedBundle"
                :products="contractProducts"
                :activityTemplates="allActivityTemplates"
                :limitToTractLevelCapture="limitToTractLevelCapture"
                :useCruiseRateForStumpage="isCruiseRateCheckboxVisible ? useCruiseRateForStumpage : false"
                :templateStats="activityTemplateStatsMap"
                :contractAccount="chosenAccount"
                :landowners="landowners"
                :namedRates="namedRates"
                :winningBid="winningBid"
                @activity-builders-updated="activityBuildersUpdated"
                @duplicate-counterparties="handleDuplicateCounterparties"
                :contractType="contractType"/>
            </LoadableComponent>
          </v-col>

          <v-col v-if="showGrossProfit" cols="12" lg="2" xl="2">
            <v-card flat>
              <v-card-title>
                <span>{{ $t('grossProfitPerTon') }}</span>
                <v-spacer/>
                <Icon
                  v-if="showPayByLoadWarning"
                  icon="mdi-information-outline"
                  :small="false"
                  :tooltipText="$t('profitPerTonCannotBeCalculatedForPayByLoad')"/>

                <Icon
                  v-if="showNonInventoryWarning"
                  icon="mdi-information-outline"
                  :small="false"
                  :tooltipText="$t('nonInventoryCostsExcluded')"/>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :items="grossProfitByProduct"
                  :headers="grossProfitByProductHeaders"
                  data-testid="gross-profit-by-product-table"
                  sort-by="amount"
                  sort-desc
                  hide-default-footer
                  dense>
                  <template #item.amount="{item}">
                    {{formatMoney(item.amount)}}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else align="center" justify="center">
          <v-col cols="auto">
            <p class="title">
              {{$t('chooseBundleToAddActivities')}}
            </p>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer/>

      <v-checkbox
        :label="$t('requiresLogger')"
        v-model="requiresLogger"
        v-if="!isStumpageTract"
        id="requires-logger"
      />

      <v-checkbox
        :label="$t('markAsDraft')"
        :disabled="lockAsDraft"
        v-model="markAsDraft"
        id="mark-as-draft"
        class="px-6"
      />

      <Icon
        v-if="missingReceivableActivity"
        icon="mdi-alert"
        iconColor="warning"
        :small="false"
        :tooltipText="$t('missingAccountsReceivableActivity')"/>

      <v-btn
        :disabled="hasDuplicateCounterparties || !selectedBundle || missingContractRequirements.length > 0"
        class="primary ml-2"
        @click="createContractAndActivities"
        id="create-contract">
        {{$t('createContract')}}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { TractDestination } from '@/model/TractDestination.js'
import { requiredFieldsForTractType, missingRequiredFields, requiredFieldMessages } from '@/utils/TractRequirements.js'
import { formatMoney } from '@/utils/NumericMutations.js'
import { mapActions, mapGetters } from 'vuex'
import { localToUTC } from '@/utils/DateFormatter.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import fieldRules from '@/utils/rules.js'
import moment from 'moment'
import {
  ContractType,
  TractTypeCategory,
  AccountingCategory,
  CostType,
  ActivityModifier,
  PayBy,
  FinancialIntegrationType,
  RecoveryMode,
  RuntimeCounterparty,
  TemplateSpecialization,
  TractTypeSpecialization,
  NamedRateSpecialization,
  BidProductStatus
} from '@/utils/Enumerations.js'
import { MAX_ACTIVITY_RATE as maxActivityRate } from '@/utils/rules'
import { TemplateSpecializationInfo } from '@/components/activity/ActivityMapping'
import {
  ActivitiesUpdateRequest, ActivityCreateRequest,
  ActivityDetailCreateRequest, CostCalculationCreateRequest
} from '@/components/activity/Activity'

export default {
  name: 'TractContractForm',

  props: {
    tract: Object,
    savedTract: {
      type: Object,
      default: null
    },
    tractDestination: TractDestination,
    landowners: []
  },

  components: {
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    SettingAutocomplete: () => import('@/components/autocomplete/ContractSettingAutocomplete.vue'),
    BundleActivityDefinition: () => import('@/components/tract/tract-form/BundleActivityDefinition'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ProductSelector: () => import('@/components/tract/tract-form/ProductSelector.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue')
  },

  data: () => ({
    rules: fieldRules.rules,
    TemplateSpecializationInfo,
    TemplateSpecialization,
    contractType: ContractType.Production,
    selectedBundle: undefined,
    activityBuilders: null,
    limitToTractLevelCapture: false,
    chosenAccount: null,
    chosenSetting: null,
    hasDuplicateCounterparties: false,
    fallbackProductId: null,
    fallbackExpands: false,
    accountProductIds: [],
    useCruiseRateForStumpage: (localStorage.getItem(LocalStorageKeys.CONTRACTS_OPERATION_USE_CRUISE_FOR_STUMPAGE) !== undefined)
      ? localStorage.getItem(LocalStorageKeys.CONTRACTS_OPERATION_USE_CRUISE_FOR_STUMPAGE) === 'true'
      : true,
    markAsDraft: (localStorage.getItem(LocalStorageKeys.CONTRACTS_OPERATION_ASDRAFT) !== undefined)
      ? localStorage.getItem(LocalStorageKeys.CONTRACTS_OPERATION_ASDRAFT) === 'true'
      : true,
    requiresLogger: localStorage.getItem(LocalStorageKeys.TRACT_CONTRACT_REQUIRES_LOGGER) === 'true',
    activityTemplateStatsMap: null,
    rateSheetDefinitionsFetched: false,
    loading: false,
    scalesTickets: false,
    contractProducts: [],
    eligibleScalingProducts: [],
    namedRates: undefined,
    winningBid: null
  }),

  computed: {
    ...mapGetters('activity-templates', ['templatesLoading', 'allTemplateBundles', 'allActivityTemplates']),
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract-type', ['allTractTypes']),

    tractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.tract.type?.tractTypeId)
    },

    // The server checks the saved tract, so unsaved edits on the tract form must not clear the warning.
    missingContractRequirements () {
      return this.missingRequirementsOn(this.savedTract ?? this.tract)
    },

    hasUnsavedRequirementChanges () {
      if (!this.savedTract) return false
      const unsaved = this.missingRequirementsOn(this.tract)
      return this.missingContractRequirements.some(field => !unsaved.includes(field))
    },
    contractTypes () {
      if (this.tractDestination.products.every(p => p.isByproduct)) return [ContractType.ByproductPurchase]
      if (this.tractDestination.products.every(p => !p.isByproduct)) return [ContractType.Production, ContractType.WoodsSale]
      return [ContractType.Production, ContractType.WoodsSale, ContractType.ByproductPurchase]
    },

    grossProfitByProductHeaders () {
      return [
        { text: this.$t('product'), value: 'productName' },
        { text: this.$t('amount'), value: 'amount', align: 'right' }
      ]
    },

    isStumpageTract () {
      return this.tract.type.category === TractTypeCategory.Stumpage.value
    },

    tractTicketBookAccounts () {
      return (this.tract.ticketBooks ?? []).map(tb => ({ accountId: tb.accountId, tooltipText: this.$t('tractTicketBookAccount') }))
    },

    nonTicketBookAccountTooltip () {
      return this.tractDestination.isExternal ? undefined : this.$t('notTractTicketBookAccount')
    },

    isInTractGroup () {
      return this.tract.tractGroupId > 0
    },

    products () {
      return this.tractDestination.products.filter(p => p.isByproduct === this.isByproductPurchaseContract)
    },

    showGrossProfit () {
      return this.contractType === ContractType.WoodsSale && !this.hasPayByVolumeInventoryActivity
    },

    hasPayByVolumeInventoryActivity () {
      return this.activityBuilders?.some(a => a.payBy === PayBy.Volume.value && a.costBehavior !== CostType.NonInventory.value)
    },

    showPayByLoadWarning () {
      return this.activityBuilders?.some(a => a.payBy === PayBy.Load.value && !this.isApOrJePenalty(a))
    },

    showScaleTicketCheckbox () {
      return this.tractDestination.products.some(p => p.isScalable) && this.activityBuilders?.some(ab => ab.payBy === PayBy.Volume.value)
    },

    showIncompatibleProductWarning () {
      if (!this.scalesTickets) return false
      return this.tractDestination.products.length !== this.eligibleScalingProducts.length
    },

    showNonInventoryWarning () {
      return this.activityBuilders?.some(a => a.costBehavior === CostType.NonInventory.value)
    },

    missingReceivableActivity () {
      return !this.activityBuilders?.some(a => a.category === AccountingCategory.Receivable.value) &&
        this.contractType.value === ContractType.WoodsSale.value
    },

    isByproductPurchaseContract () {
      return this.contractType.value === ContractType.ByproductPurchase.value
    },

    grossProfitByProduct () {
      if (this.selectedBundle === undefined ||
        this.contractType === ContractType.Production ||
        this.activityBuilders === null ||
        this.showPayByLoadWarning) {
        return []
      }

      const updateProductAmount = (product, amount, activity) => {
        const factor = activity.category === AccountingCategory.Receivable.value ? 1 : -1
        return { ...product, amount: product.amount + amount * factor }
      }

      const createProductEntry = (product, amount, activity) => {
        const factor = activity.category === AccountingCategory.Receivable.value ? 1 : -1
        return {
          productName: product.productName,
          productId: product.productId,
          amount: amount * factor
        }
      }

      const addProductToMap = (map, product, amount, activity) => {
        const existingProduct = map.get(product.productId)
        if (existingProduct) {
          map.set(product.productId, updateProductAmount(existingProduct, amount, activity))
        } else {
          map.set(product.productId, createProductEntry(product, amount, activity))
        }
      }

      const getProductsWithAmounts = (activity, products) => {
        if (activity.products.length < products.length) {
          return products.map(p => {
            const definedProduct = activity.products.find(ap => ap.productId === p.productId)
            return definedProduct ? { ...definedProduct } : { ...p, baseRate: activity.baseRate }
          })
        }

        return activity.products.map(p => ({ ...p }))
      }

      const map = this.activityBuilders
        .filter(ab => ab.costBehavior !== CostType.NonInventory.value && !this.isApOrJePenalty(ab))
        .reduce((productMap, activity) => {
          const productsWithAmounts = getProductsWithAmounts(activity, this.products)

          productsWithAmounts.forEach(p => {
            const amount = activity.modifier === ActivityModifier.DistanceTraveled.value
              ? p.baseRate * this.tractDestination.distance
              : p.baseRate
            addProductToMap(productMap, p, amount, activity)
          })

          return productMap
        }, new Map())

      return Array.from(map.values())
    },

    title () {
      return `New Contract: ${this.tract.name} &rarr; ${this.tractDestination.destinationAccountName}`
    },

    isCruiseRateCheckboxVisible () {
      if (this.selectedBundle === undefined || this.tract.type.category === 1) {
        return false
      }

      const suitableActivity = this.selectedBundle.activityBuilders.find(a => a.category !== 1 && a.costBehavior === 2)
      return suitableActivity !== undefined
    },

    lockAsDraft () {
      return this.companyInfo.requireApprovalForContractModifications
    },

    mappedTemplateBundlesWithWarning () {
      const nullOrUndefined = (val) => { return val === null || val === undefined }
      const hasObligationRate = (this.namedRates ?? []).some(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
      const allowedBundleIds = (this.tractType?.templateBundles ?? []).map(b => b.templateBundleId)
      return this.allTemplateBundles
        .filter(tb => allowedBundleIds.length === 0 || allowedBundleIds.includes(tb.templateBundleId))
        .filter(tb => this.tract.type.specialization === TractTypeSpecialization.Depletion.value || !tb.activityBuilders.some(b => b.specialization === TemplateSpecialization.Depletion.value))
        .map(tb => {
          const hasObligationBuilder = tb.activityBuilders.some(b => b.specialization === TemplateSpecialization.Obligation.value)
          const missingFields = tb.activityBuilders.reduce((set, builder) => {
            if (!nullOrUndefined(builder.runtimeField) && !this.runtimeCounterpartyAssigned(builder.runtimeField, this.tract, this.tractDestination)) {
              set.add(RuntimeCounterparty.fromInt(builder.runtimeField).toLowerCase())
            }
            if (builder.modifier === ActivityModifier.DistanceTraveled.value && (this.tractDestination.distance === null || this.tractDestination.distance === 0)) {
              set.add(this.$t('milesToDestination'))
            }
            return set
          }, new Set())
          return {
            ...tb,
            missingObligationRate: hasObligationBuilder && !hasObligationRate,
            missingTractFieldsString: missingFields.size > 0 ? Array.from(missingFields).join(', ') : undefined
          }
        })
    },

    bundlePaysByVolume () {
      return !!this.selectedBundle?.activityBuilders.some(ab => ab.payBy === PayBy.Volume.value)
    }
  },

  watch: {
    useCruiseRateForStumpage (val) {
      localStorage.setItem(LocalStorageKeys.CONTRACTS_OPERATION_USE_CRUISE_FOR_STUMPAGE, val)
    },

    limitToTractLevelCapture (val) {
      localStorage.setItem(LocalStorageKeys.LIMIT_TO_TRACT_LEVEL_CAPTURE, val)
    },

    markAsDraft (mark) {
      if (mark) {
        localStorage.setItem(LocalStorageKeys.CONTRACTS_OPERATION_ASDRAFT, 'true')
      } else {
        localStorage.setItem(LocalStorageKeys.CONTRACTS_OPERATION_ASDRAFT, 'false')
      }
    },

    requiresLogger (val) {
      localStorage.setItem(LocalStorageKeys.TRACT_CONTRACT_REQUIRES_LOGGER, val)
    },

    isByproductPurchaseContract (val) {
      if (val) this.chosenSetting = null
    },

    async selectedBundle (val) {
      if (val.activityBuilders.some(ab => ab.payBy === PayBy.Volume.value)) {
        await this.getRateSheetDefinitions()
      }
    }
  },

  created () {
    if (this.allTractTypes.length === 0) this.fetchTractTypes()
    this.setInitialValues()
  },

  methods: {
    missingRequirementsOn (tract) {
      return missingRequiredFields(tract, requiredFieldsForTractType(this.tractType), {
        landowners: this.landowners?.length > 0 ? this.landowners : undefined
      })
    },
    ...mapActions('activity-templates', ['fetchTemplateBundles', 'fetchActivityTemplates', 'fetchActivityTemplateStatsById']),
    ...mapActions('contract', ['createContract', 'updateContractProducts']),
    ...mapActions('activity', ['createActivity', 'updateActivities']),
    ...mapActions('activity-detail', ['createActivityDetail']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions']),
    ...mapActions('tract', ['fetchNamedRates']),
    ...mapActions('account', ['fetchAccountById']),
    ...mapActions('bid', ['fetchWinningBids']),
    ...mapActions('tract-type', ['fetchTractTypes']),
    formatMoney,
    requiredFieldMessages,

    async setInitialValues () {
      await this.loadWinningBid()
      const destinationAccount = await this.fetchAccountById(this.tractDestination.destinationAccountId)
      this.fallbackExpands = destinationAccount.fallbackExpands
      this.fallbackProductId = destinationAccount.fallbackProductId
      const scaleProducts = destinationAccount.accountProducts
        ? destinationAccount.accountProducts.filter(p => p.isScalable)
        : (destinationAccount.scalingProducts ?? [])
      this.accountProductIds = scaleProducts.map(p => p.productId)

      this.contractProducts = [...this.tractDestination.products].map(p => {
        const serverFreq = parseFloat(p.scaleFrequency)
        const displayFreq = serverFreq && serverFreq > 0 ? Math.round(100 / serverFreq) : 0

        return {
          ...p,
          scaleFrequency: displayFreq,
          name: p.productName
        }
      })

      this.eligibleScalingProducts = this.contractProducts.filter(p => p.isScalable &&
        ((p.productId !== this.fallbackProductId || (this.fallbackExpands && p.productId === this.fallbackProductId)) ||
        this.accountProductIds.includes(p.productId)))

      this.contractType = this.tractDestination.isExternal
        ? ContractType.WoodsSale
        : this.tractDestination.products.some(p => !p.isByproduct)
          ? ContractType.Production
          : ContractType.ByproductPurchase

      const cookie = localStorage.getItem(LocalStorageKeys.LIMIT_TO_TRACT_LEVEL_CAPTURE)
      if (cookie) {
        this.limitToTractLevelCapture = cookie === 'true'
      }

      if (this.companyInfo.requireApprovalForContractModifications) this.markAsDraft = true
      this.fetchTemplateBundles()
      this.fetchActivityTemplates()
      this.loadNamedRates()
    },

    async loadNamedRates () {
      if (this.isInTractGroup) {
        this.namedRates = await this.fetchNamedRates(this.tract.tractGroupId)
      }
    },

    async loadWinningBid () {
      try {
        const bids = await this.fetchWinningBids(this.tract.tractId)
        const matchingBids = bids.filter(b => b.destinationAccountId === this.tractDestination.destinationAccountId)
        if (matchingBids.length === 0) return
        const rateByProductId = {}
        const volumeByProductId = {}
        for (const bid of matchingBids) {
          for (const product of (bid.products ?? []).filter(p => p.status !== BidProductStatus.Lost.value)) {
            const volume = product.harvestVolume ?? 0
            if (!(product.productId in rateByProductId) || volume > volumeByProductId[product.productId]) {
              rateByProductId[product.productId] = product.deliveredRate
              volumeByProductId[product.productId] = volume
            }
          }
        }
        if (Object.keys(rateByProductId).length === 0) return
        this.winningBid = {
          supplierAccountId: matchingBids[0].supplierAccountId,
          rateByProductId
        }
      } catch {}
    },

    accountChosen (account) {
      this.chosenAccount = account
    },

    settingChosen (setting) {
      this.chosenSetting = setting
    },

    activityBuildersUpdated (activityBuilders) {
      this.activityBuilders = [...activityBuilders]
    },

    close () {
      this.$emit('close', false)
    },

    validateForm () {
      if (this.chosenAccount === null) {
        return this.$t('youMustSpecifyAnAccount')
      }

      if (this.chosenSetting === null && !this.isByproductPurchaseContract) {
        return this.$t('youMustSpecifyASetting')
      }

      if (this.activityBuilders === null) {
        return null
      }

      const counterparties = this.activityBuilders.map(b => ({ ...b.counterparty, category: b.category, runtimeField: b.runtimeField }))

      for (const counterparty of counterparties) {
        if ((counterparty?.accountId === null || counterparty?.accountId === undefined) &&
          (counterparty.runtimeField === undefined || counterparty.runtimeField === null)) {
          return this.$t('youMustSpecifyACounterparty')
        }

        if (counterparty?.exportCode === '' &&
          counterparty?.category !== AccountingCategory.Accrual.value &&
          this.companyInfo.financialIntegrationType !== FinancialIntegrationType.None.value &&
          !this.markAsDraft) {
          return this.$t('counterpartyExportCodeErrorActivity', { accountName: counterparty.name })
        }
      }

      for (const builder of this.activityBuilders
        .filter(b => b.payBy !== PayBy.Volume.value)
        .filter(b => !b.limitToBaseRate)) {
        const minBaseRate = builder.baseRate
        for (const product of builder.products) {
          if (product.baseRate < minBaseRate) {
            return this.$t('productBaseRatesMustBeGreaterThanOrEqualToActivityBaseRate')
          }
        }
      }

      if (this.activityBuilders.some(ab => ab.baseRate > maxActivityRate) ||
        this.activityBuilders.some(ab => ab.products.some(p => p.baseRate > maxActivityRate))) {
        return this.$t('invalidActivityRate')
      }

      if (this.existsVolumeActivityWithNoRateSheet()) {
        return this.$t('volumeActivityHasNoRateSheet')
      }

      if (this.eligibleScalingProducts.some(p => p.scaleFrequency > 0 && (!Number.isInteger(Number(p.scaleFrequency)) || p.scaleFrequency < 0 || p.scaleFrequency > 100))) {
        return this.$t('invalidScaleFrequencyError')
      }

      return null
    },

    existsVolumeActivityWithNoRateSheet () {
      return this.activityBuilders.some(ab => {
        const isByVolume = ab.template?.baseCost?.payBy === PayBy.Volume.value
        return isByVolume && ab.rateSheet == null
      })
    },

    handleDuplicateCounterparties (duplicates) {
      this.hasDuplicateCounterparties = Object.keys(duplicates).length > 0
    },

    async createContractAndActivities () {
      const validationError = this.validateForm()
      if (validationError !== null) {
        this.setSnackError(validationError)
        return
      }

      const harvestEndDate = (this.tract.harvestEndDate === null || this.tract.harvestEndDate === undefined)
        ? moment().add(3, 'months').format()
        : this.tract.harvestEndDate

      const contractCreateRequest = {
        type: this.contractType.value,
        accountId: this.chosenAccount.accountId,
        settingId: this.chosenSetting?.settingId,
        destinationAccountId: this.tractDestination.destinationAccountId,
        tractId: this.tract.tractId,
        requiresExt1: this.tract.requiresExt1,
        requiresExt2: this.tractDestination.requiresExt2,
        requiresLogger: this.isStumpageTract ? true : this.requiresLogger,
        effectiveDate: localToUTC(this.tract.harvestStartDate),
        expirationDate: localToUTC(harvestEndDate),
        distance: this.tractDestination.distance,
        isDraft: this.markAsDraft
      }

      const contractResponse = await this.createContract(contractCreateRequest)

      const findScaleFrequency = (productId) => {
        const product = this.eligibleScalingProducts.find(p => p.productId === productId)
        return product ? product.scaleFrequency : 0
      }

      const contractProducts = this.contractProducts.sort((a, b) => a.name.localeCompare(b.name)).map((p, idx) => ({
        productId: p.productId,
        sortOrder: idx,
        scaleFrequency: this.scalesTickets
          ? (findScaleFrequency(p.productId) > 0
            ? parseFloat((100 / findScaleFrequency(p.productId)).toFixed(3))
            : null)
          : null
      }))

      await this.updateContractProducts({ contractId: contractResponse.contractId, products: contractProducts })
      if (contractResponse) {
        await this.createActivities(contractResponse, contractCreateRequest)
      }
      this.$emit('close', true)
    },

    async createActivities (contractResponse, contractCreateRequest) {
      const builderById = this.activityBuilders.reduce((map, builder) => {
        map.set(builder.activityBuilderId, builder)
        return map
      }, new Map())

      const deductedBuilderIdentifierById = new Map()
      this.activityBuilders.forEach(builder => {
        if (builder.deductingBuilderIds?.length > 0) {
          deductedBuilderIdentifierById.set(builder.activityBuilderId, {
            accountId: builder.counterparty?.accountId,
            runtimeCounterparty: builder.runtimeField,
            activityTemplateId: builder.activityTemplateId
          })
          builder.deductingBuilderIds.forEach(deductingActivityId => {
            const deductingBuilder = builderById.get(deductingActivityId)
            deductingBuilder.deductFromActivityId = builder.activityBuilderId
            builderById.set(deductingActivityId, deductingBuilder)
          })
        }
      })

      const activitiesUpdateRequest = new ActivitiesUpdateRequest({
        contractId: contractResponse.contractId,
        deletedActivityIds: [],
        updatedActivities: [],
        newActivities: Array.from(builderById.values()).map(activity => {
          const baseCost = this.showBaseCost(activity) === false
            ? null
            : new CostCalculationCreateRequest({
              rate: activity.baseRate,
              payOn: activity.payOn,
              payBy: activity.payBy,
              rateSheet: activity.rateSheet
            })

          const activityDetails = activity.products.map(detail =>
            new ActivityDetailCreateRequest({
              productId: detail.productId,
              rate: detail.baseRate
            })
          )

          const deductFromActivityIdentifier = activity.deductFromActivityId == null
            ? null
            : deductedBuilderIdentifierById.get(activity.deductFromActivityId)

          return new ActivityCreateRequest({
            contractId: contractResponse.contractId,
            templateId: activity.activityTemplateId,
            effectiveDate: contractCreateRequest.effectiveDate,
            recoveryMode: activity.recoveryMode.value,
            advanceId: activity.recoveryMode.value !== RecoveryMode.None.value ? activity.advanceId : undefined,
            baseCost,
            runtimeCounterparty: activity.runtimeField,
            contractorId: activity.counterparty?.accountId,
            activityDetails,
            deductFromActivityIdentifier,
            namedRateId: activity.namedRateId
          })
        })
      })

      await this.updateActivities(activitiesUpdateRequest)
    },

    runtimeCounterpartyAssigned (runtimeField, tract, tractDestination) {
      const nullOrUndefined = (val) => { return val === null || val === undefined }
      switch (runtimeField) {
        case RuntimeCounterparty.Driver.value:
          return true
        case RuntimeCounterparty.Landowner.value:
          return tract.landownerCount > 0
        case RuntimeCounterparty.DefaultLogger.value:
          return !nullOrUndefined(tract.loggerAccountId)
        case RuntimeCounterparty.ContractAccount.value:
          return !nullOrUndefined(this.chosenAccount?.accountId)
        case RuntimeCounterparty.Hauler.value:
          return !nullOrUndefined(tract.haulerAccountId)
        case RuntimeCounterparty.Supplier.value:
          return !nullOrUndefined(tract.supplierAccountId)
        case RuntimeCounterparty.DestinationAccount.value:
          return !nullOrUndefined(tractDestination.destinationAccountId)
        case RuntimeCounterparty.ConsultingForester.value:
          return !nullOrUndefined(tract.consultingForesterAccountId)
      }
    },

    async templateSelected () {
      this.loading = true
      const templateStats = new Map()

      this.selectedBundle.activityBuilders.forEach(async ab => {
        templateStats.set(ab.activityTemplateId, await this.fetchActivityTemplateStatsById(ab.activityTemplateId))
      })

      this.activityTemplateStatsMap = templateStats

      this.contractProducts = [...this.tractDestination.products].filter(p => (p.isScalable && this.bundlePaysByVolume) || !this.bundlePaysByVolume).map(p => {
        const serverFreq = parseFloat(p.scaleFrequency)
        const displayFreq = serverFreq && serverFreq > 0 ? Math.round(100 / serverFreq) : 0

        return {
          ...p,
          scaleFrequency: displayFreq,
          name: p.productName
        }
      })

      this.$nextTick(() => {
        this.loading = false
      })
    },

    showBaseCost (activity) {
      return activity.specialization !== TemplateSpecialization.Transfer.value &&
        (activity.specialization !== TemplateSpecialization.Penalty.value || activity.category === AccountingCategory.Receivable.value)
    },

    isApOrJePenalty (ab) {
      return TemplateSpecialization.Penalty.value === ab.specialization && [AccountingCategory.Payable.value, AccountingCategory.Accrual.value].includes(ab.category)
    },

    async getRateSheetDefinitions () {
      if (!this.rateSheetDefinitionsFetched) {
        this.loading = true
        await this.fetchAccountRateSheetDefinitions(this.tractDestination.destinationAccountId)
        this.rateSheetDefinitionsFetched = true
        this.loading = false
      }
    },

    contractProductsSelected (products) {
      this.contractProducts = [...this.tractDestination.products].filter(p => products.some(sp => sp.productId === p.productId)).map(p => {
        const serverFreq = parseFloat(p.scaleFrequency)
        const displayFreq = serverFreq && serverFreq > 0 ? Math.round(100 / serverFreq) : 0

        return {
          ...p,
          scaleFrequency: displayFreq,
          name: p.productName
        }
      })

      this.eligibleScalingProducts = this.contractProducts.filter(p => p.isScalable &&
        ((p.productId !== this.fallbackProductId || (this.fallbackExpands && p.productId === this.fallbackProductId)) ||
        this.accountProductIds.includes(p.productId)))
    }
  }
}
</script>
